/**
 * product-core.js
 * ---------------------------------------------------------------------------
 * Local replacement for the two PDP custom elements that used to be supplied by
 * the remote vendor bundle (the vendor runtime):
 *
 *   <product-form>  — add-to-cart, rendered from 8 different snippets
 *   <product-info>  — the PDP info container (sections/main-product.liquid:113)
 *
 * Standard behaviour is modelled on Shopify Dawn (MIT), adapted to the exact
 * DOM this theme renders. Every selector below was read out of this theme's
 * Liquid; the file + line is quoted next to each one.
 *
 * PUBLIC API (other clusters may rely on these names)
 *
 *   product-form
 *     .handleSubmit(event)              REQUIRED NAME — layout/theme.liquid:702-709
 *                                       proxies every [id^="SectionAtcBtn-"] click here.
 *     .toggleAddButton(disable, text, modifyClass)   Dawn-12 compatible signature.
 *     .updateButtonPrice(text)          writes the in-button price spans.
 *     .setErrorMessage(text) / .clearErrorMessage()
 *     .variantIdInput                   the live <input name="id">
 *
 *   product-info
 *     .initShareLinks()                 REQUIRED NAME — assets/media-gallery.js:13
 *     .sectionId                        quick-add-safe section id
 *     .sectionUrl(variantId)            builds the ?variant=&section_id= URL
 *     .toggleQuantityLoading(bool)      drives the CSS hook in assets/base.css:1
 *
 * NOT OWNED HERE (deliberately):
 *   - the variant change / section re-render. <variant-selects> carries the full
 *     Dawn-12 attribute set (snippets/product-variant-picker.liquid:21-38:
 *     data-url, data-section, data-update-url, data-skip-non-existent,
 *     data-skip-unavailable, data-has-filtering) and <product-info> carries none
 *     of it, so the fetch-and-swap belongs to <variant-selects>.
 *   - trapFocus / removeTrapFocus / debounce / pauseAllMedia. Those are consumed
 *     by assets/secondary.js, cart-notification.js, pickup-availability.js,
 *     facets.js and media-gallery.js and must be defined by exactly one shared
 *     module in the rebuild. This file never defines and never needs them.
 *
 * Vanilla ES2019. No imports, no build step.
 */

(function () {
  'use strict';

  /* =======================================================================
   * Small local helpers. Namespaced so they cannot collide with anything in
   * assets/secondary.js (which already owns top-level `isRtl`,
   * `defaultDirection` and `focusVisiblePolyfill`).
   * ===================================================================== */

  /** Run fn, swallow and log any failure. Used for every non-critical step so
   *  a broken side effect can never break add-to-cart. */
  function pcSafe(fn, label) {
    try {
      return fn();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[product-core] ' + (label || 'step') + ' failed:', err);
      return undefined;
    }
  }

  /** The cart UI element. cart-notification renders only when
   *  settings.cart_type == 'notification' (sections/header.liquid:791);
   *  cart-drawer only when == 'drawer' (layout/theme.liquid:650-652). */
  function pcCartTarget() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function pcRoutes() {
    return window.routes || {};
  }

  /** Locale-aware checkout URL derived from window.routes.cart_url
   *  (layout/theme.liquid:677). '/cart' -> '/checkout', '/fr/cart' -> '/fr/checkout'. */
  function pcCheckoutUrl() {
    var cartUrl = pcRoutes().cart_url || '/cart';
    return cartUrl.replace(/\/cart\/?$/, '') + '/checkout';
  }

  function pcCartError() {
    return (window.cartStrings && window.cartStrings.error) || 'There was an error.';
  }

  /** Inline style helpers. `.main-atc__error` (snippets/buy-buttons.liquid:126)
   *  and `.sticky-atc__error` (sections/main-product.liquid:653) ship with an
   *  inline style="display:none;" and have ZERO rules in base.css / rtl.css, so
   *  adding `.hidden` would not win — we must drive el.style.display. */
  function pcShow(el, display) {
    if (el) el.style.display = display || 'block';
  }

  function pcHide(el) {
    if (el) el.style.display = 'none';
  }

  /* =======================================================================
   * <product-form>
   * ===================================================================== */

  var ProductFormElement = function (BaseElement) {
    return class extends BaseElement {
      connectedCallback() {
        if (this.pcInitialised) return;
        this.pcInitialised = true;

        // The {% form 'product' %} output. Present at all 8 render sites, e.g.
        // buy-buttons.liquid:80, card-product.liquid:421, bundle-offer.liquid:655,
        // separate-atc-btn.liquid:5, quantity-breaks.liquid:580, cart-gift.liquid:204,
        // quantity-gifts.liquid:116, upsell-block.liquid:354.
        this.form = this.querySelector('form');
        if (!this.form) return;

        // card-product.liquid:434, separate-atc-btn.liquid:17 and
        // bundle-offer.liquid:667 render the variant input disabled; the PDP
        // (buy-buttons.liquid:88-96) does not. Dawn enables it on connect.
        this.variantIdInput = this.form.querySelector('[name="id"]');
        if (this.variantIdInput) this.variantIdInput.disabled = false;

        // Only buy-buttons.liquid:65-78 and bundle-offer.liquid:641-654 render
        // the Dawn error wrapper — every access must stay null-guarded.
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.querySelector('.product-form__error-message');

        // quantity-breaks.liquid:578, cart-gift.liquid:203, quantity-gifts.liquid:115,
        // upsell-block.liquid:353 — these live inside the drawer / hidden blocks.
        this.isCartUpsell = this.dataset.isCartUpsell === 'true';

        // data-main="true" only on the PDP form (buy-buttons.liquid:63); it is the
        // only render site where data-section is a REAL Shopify section id
        // (buy-buttons.liquid:61 vs the composite keys in bundle-offer.liquid:640,
        // quantity-gifts.liquid:115, upsell-block.liquid:353).
        this.isMain = this.dataset.main === 'true';
        this.sectionId = this.isMain ? this.dataset.section : null;

        this.pcOnSubmit = this.handleSubmit.bind(this);
        this.form.addEventListener('submit', this.pcOnSubmit);

        pcSafe(this.pcObservePriceBlock.bind(this), 'price observer');
      }

      disconnectedCallback() {
        if (this.form && this.pcOnSubmit) this.form.removeEventListener('submit', this.pcOnSubmit);
        if (this.pcPriceObserver) this.pcPriceObserver.disconnect();
        this.pcPriceObserver = null;
        this.pcInitialised = false;
      }

      /* ---------------------------------------------------------------
       * Scope helpers
       * ------------------------------------------------------------- */

      /** Everything on the PDP that this form talks to lives inside the same
       *  <product-info> (sections/main-product.liquid:113). Scoping through it
       *  keeps us safe inside the quick-add modal, where assets/quick-add.js:109
       *  clones the whole subtree with rewritten ids. */
      get pcScope() {
        return this.closest('product-info') || document;
      }

      /** The sticky ATC (sections/main-product.liquid:453) is a sibling INSIDE
       *  product-info, so pcScope covers it too. */
      pcQueryScope(selector) {
        try {
          return this.pcScope.querySelector(selector);
        } catch (err) {
          return null;
        }
      }

      pcQueryScopeAll(selector) {
        try {
          return Array.prototype.slice.call(this.pcScope.querySelectorAll(selector));
        } catch (err) {
          return [];
        }
      }

      /* ---------------------------------------------------------------
       * Submit
       * ------------------------------------------------------------- */

      /**
       * FIXED PUBLIC NAME. Handles both:
       *   - a real `submit` event on the inner <form>, and
       *   - a `click` event on an arbitrary external button, proxied by
       *     layout/theme.liquid:704-708 for every [id^="SectionAtcBtn-"]
       *     (the sticky ATC at sections/main-product.liquid:643, the secondary
       *     skip-cart button at buy-buttons.liquid:164, and the section ATC
       *     buttons in image-with-text / icons-with-content / rich-text /
       *     custom-columns / results / main-article).
       */
      handleSubmit(evt) {
        if (evt && typeof evt.preventDefault === 'function') evt.preventDefault();
        if (!this.form) return;

        var button = this.pcResolveButton(evt);

        // Dawn's re-entrancy guard.
        if (button && button.getAttribute('aria-disabled') === 'true') return;

        // Required custom fields gate (buy-buttons.liquid:120-121).
        if (!this.pcCustomFieldsValid(button)) return;

        this.clearErrorMessage();

        var loading = this.pcStartLoading(button);
        var self = this;
        var skipCart = this.pcShouldSkipCart(button);

        var payload;
        try {
          payload = this.pcBuildPayload();
        } catch (err) {
          this.pcStopLoading(loading);
          this.setErrorMessage(pcCartError(), button);
          return;
        }

        if (!payload) {
          // Nothing to add (e.g. bundle-offer.liquid:663-672 renders value="" and
          // its owning component had not populated a variant yet).
          this.pcStopLoading(loading);
          this.setErrorMessage(pcCartError(), button);
          return;
        }

        var cart = pcCartTarget();

        // assets/cart-notification.js:74-76 stores it, :30 uses it in
        // removeTrapFocus(this.activeElement). assets/quick-add.js:9-10 relies on
        // the same method existing on cart-notification AND cart-drawer.
        if (cart && !this.isCartUpsell && typeof cart.setActiveElement === 'function') {
          pcSafe(function () {
            cart.setActiveElement(document.activeElement);
          }, 'setActiveElement');
        }

        // Section Rendering ride-along on the same POST.
        if (cart && typeof cart.getSectionsToRender === 'function') {
          pcSafe(function () {
            var ids = cart
              .getSectionsToRender()
              .map(function (section) {
                return section.id;
              })
              .filter(Boolean)
              .join(',');
            if (ids) {
              payload.append('sections', ids);
              payload.append('sections_url', window.location.pathname);
            }
          }, 'sections param');
        }

        // MUST go through the live window.fetch: snippets/free-gifts.liquid:915-928
        // replaces window.fetch and matches CART_WRITE (free-gifts.liquid:494) to
        // refresh <gift-progress>. Caching a native reference silently breaks it.
        window
          .fetch(pcRoutes().cart_add_url || '/cart/add', {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: payload
          })
          .then(function (response) {
            return response.json();
          })
          .then(function (parsedState) {
            // Shopify returns { status: 422, description: '...' } on failure.
            if (parsedState && parsedState.status) {
              self.pcHandleCartError(parsedState, button);
              self.pcStopLoading(loading);
              return;
            }

            var primary = parsedState && parsedState.items ? parsedState.items[0] : parsedState;

            pcSafe(function () {
              self.pcSyncQuantityRules(primary);
            }, 'quantity rules sync');

            // Belt-and-braces for listeners that cannot see the fetch:
            // snippets/free-gifts.liquid:948-951 listens for this.
            pcSafe(function () {
              document.dispatchEvent(
                new CustomEvent('cart:updated', { detail: { source: 'product-form' } })
              );
            }, 'cart:updated');

            if (skipCart) {
              // Schema info, sections/main-product.liquid (buy_buttons.skip_cart):
              // "Your customers will be sent directly to checkout after click the
              // Add to Cart button." Keep the spinner while navigating.
              window.location.href = pcCheckoutUrl();
              return;
            }

            if (cart && typeof cart.renderContents === 'function') {
              pcSafe(function () {
                var state = primary || {};
                if (parsedState && parsedState.sections && !state.sections) {
                  state = Object.assign({}, state, { sections: parsedState.sections });
                }
                // Cart-upsell forms must refresh the drawer without opening it
                // and without stealing focus (dontOpen === true).
                cart.renderContents(state, self.isCartUpsell);
              }, 'renderContents');
            } else if (!self.isCartUpsell) {
              // settings.cart_type == 'page' — no cart element exists.
              window.location.href = pcRoutes().cart_url || '/cart';
              return;
            }

            self.pcStopLoading(loading);
          })
          .catch(function (err) {
            // eslint-disable-next-line no-console
            console.error('[product-core] add to cart failed:', err);
            self.setErrorMessage(pcCartError(), button);
            self.pcStopLoading(loading);
          });
      }

      /** The button that actually originated the event — never blindly the
       *  form's own [type="submit"]. */
      pcResolveButton(evt) {
        if (evt) {
          if (evt.submitter) return evt.submitter;
          var current = evt.currentTarget;
          if (current && current.tagName === 'BUTTON') return current;
          if (evt.target && typeof evt.target.closest === 'function') {
            var fromTarget = evt.target.closest('button');
            if (fromTarget) return fromTarget;
          }
        }
        return this.querySelector('[type="submit"]');
      }

      /* ---------------------------------------------------------------
       * Payload
       * ------------------------------------------------------------- */

      /**
       * new FormData(this.form) automatically picks up the inputs that are
       * associated from OUTSIDE <product-form> through the form="" attribute:
       *   - input#Quantity-<sectionId> (sections/main-product.liquid:390-405)
       *   - select#Option-<sectionId>-<n> name="options[...]"
       *     (snippets/product-variant-picker.liquid:82-89 and
       *      sections/main-product.liquid:560-565)
       * The options[...] keys are ignored by /cart/add — Dawn ships the same way.
       */
      pcBuildPayload() {
        var formData = new FormData(this.form);
        var primaryId = formData.get('id');
        var extras = this.pcCollectExtraItems();

        if (!primaryId && !extras.length) return null;

        if (!extras.length) return formData;

        // Multi-item add: /cart/add accepts a form-encoded items[] array.
        var multi = new FormData();
        var quantity = formData.get('quantity') || 1;
        var index = 0;

        if (primaryId) {
          multi.append('items[0][id]', primaryId);
          multi.append('items[0][quantity]', quantity);
          formData.forEach(function (value, key) {
            if (key.indexOf('properties[') === 0) {
              multi.append('items[0][' + key + ']', value);
            } else if (key === 'selling_plan') {
              multi.append('items[0][selling_plan]', value);
            }
          });
          index = 1;
        }

        extras.forEach(function (item) {
          multi.append('items[' + index + '][id]', item.id);
          multi.append('items[' + index + '][quantity]', item.quantity);
          index += 1;
        });

        return multi;
      }

      /**
       * `.product-form__variants` is the staging area for extra variant ids:
       *   buy-buttons.liquid:87 / card-product.liquid:428 / separate-atc-btn.liquid:12
       *     -> <div class="hidden product-form__variants" data-values="">
       *   bundle-offer.liquid:662 -> <div class="product-form__variants"> (no data-values)
       * It always renders EMPTY, so the encoding is not fixed by the Liquid.
       * We accept the three shapes a sibling component could plausibly write,
       * and fall straight back to the plain single-item POST when it is empty.
       */
      pcCollectExtraItems() {
        var items = [];
        var staging = this.querySelector('.product-form__variants');

        if (staging) {
          var raw = (staging.dataset.values || '').trim();
          if (raw) {
            if (raw.charAt(0) === '[') {
              pcSafe(function () {
                JSON.parse(raw).forEach(function (entry) {
                  if (entry && typeof entry === 'object') {
                    items.push({ id: entry.id, quantity: entry.quantity || 1 });
                  } else {
                    items.push({ id: entry, quantity: 1 });
                  }
                });
              }, 'data-values JSON');
            } else {
              raw.split(',').forEach(function (entry) {
                var parts = String(entry).trim().split(':');
                if (!parts[0]) return;
                items.push({ id: parts[0], quantity: parseInt(parts[1], 10) || 1 });
              });
            }
          }

          // Any nodes a sibling component injected into the staging div.
          Array.prototype.slice.call(staging.querySelectorAll('[data-id]')).forEach(function (node) {
            items.push({ id: node.dataset.id, quantity: parseInt(node.dataset.quantity, 10) || 1 });
          });
        }

        // bundle-offer.liquid:640 — "Include product page upsells with a separate
        // offer". The PDP upsells are <product-info-upsell data-selected data-id>
        // (snippets/upsell-block.liquid:159-165, rendered from main-product.liquid:888).
        if (this.dataset.includePdpUpsells === 'true') {
          Array.prototype.slice
            .call(document.querySelectorAll('product-info-upsell[data-selected="true"][data-id]'))
            .forEach(function (node) {
              items.push({ id: node.dataset.id, quantity: 1 });
            });
        }

        // Drop empties and merge duplicates.
        var merged = [];
        var seen = {};
        items.forEach(function (item) {
          if (!item || !item.id) return;
          var key = String(item.id);
          if (seen[key] !== undefined) {
            merged[seen[key]].quantity += item.quantity;
          } else {
            seen[key] = merged.length;
            merged.push({ id: key, quantity: item.quantity });
          }
        });
        return merged;
      }

      /**
       * Skip cart -> straight to checkout. Two triggers:
       *   input.product-variant-id[data-skip-cart="true"]
       *     buy-buttons.liquid:93-95 / separate-atc-btn.liquid:19-21 / bundle-offer.liquid:669-671
       *   button[data-skip-cart-button='true']  buy-buttons.liquid:168
       */
      pcShouldSkipCart(button) {
        if (this.isCartUpsell) return false;
        if (button && button.dataset && button.dataset.skipCartButton === 'true') return true;
        var input = this.form.querySelector('input[name="id"][data-skip-cart="true"]');
        return !!input;
      }

      /* ---------------------------------------------------------------
       * Required custom fields gate
       * ------------------------------------------------------------- */

      /**
       * The submit button carries the counters (buy-buttons.liquid:120-121, both
       * start at "0"); <custom-product-field> owns them
       * (snippets/custom-product-field.liquid:13-29).
       *
       * If a field element exposes a validate() method we prefer it; otherwise we
       * fall back to the counters. The message is the LAST required field's
       * data-atc-error-msg — the schema says so explicitly
       * (sections/main-product.liquid:10726-10729: "If multiple required fields
       * exist, the message from the last one will be applied.").
       */
      pcCustomFieldsValid(button) {
        var fields = this.pcQueryScopeAll('custom-product-field[data-required="true"]');
        var invalid = false;

        if (fields.length) {
          fields.forEach(function (field) {
            if (typeof field.validate === 'function') {
              if (field.validate() === false) invalid = true;
            }
          });
        }

        if (!invalid && button && button.dataset && button.dataset.requiredFields !== undefined) {
          var required = parseInt(button.dataset.requiredFields, 10) || 0;
          var valid = parseInt(button.dataset.validFields, 10) || 0;
          if (required > valid) invalid = true;
        }

        if (!invalid) return true;

        var last = fields.length ? fields[fields.length - 1] : null;
        var message = (last && last.dataset.atcErrorMsg) || '';
        // sections/main-product.liquid:10740-10743 — sticky_atc_error_msg is a
        // CHECKBOX ("Apply error message to Sticky ATC"), so the value is a flag.
        var applySticky = !!(last && last.dataset.applyStickyAtcError === 'true');

        this.pcShowInButtonError(message, applySticky, button);
        return false;
      }

      /**
       * Shows the message inside the ATC button itself. `.main-atc__error`
       * (buy-buttons.liquid:126) and `.sticky-atc__error` (main-product.liquid:653)
       * carry inline display:none and have no CSS at all, so we drive
       * style.display directly and hide the label while the message shows.
       */
      pcShowInButtonError(message, applySticky, button) {
        if (!message) return;
        var self = this;

        var targets = [];
        var mainError = this.querySelector('.main-atc__error');
        var mainLabel = this.querySelector('.main-atc__label');
        if (mainError) targets.push({ error: mainError, label: mainLabel });

        if (applySticky) {
          var stickyError = this.pcQueryScope('.sticky-atc__error');
          var stickyLabel = this.pcQueryScope('.sticky-atc__label');
          if (stickyError) targets.push({ error: stickyError, label: stickyLabel });
        }

        // The originating button may be a section ATC with neither span
        // (sections/image-with-text.liquid:152-172) — nothing to do then.
        if (!targets.length) {
          if (button) this.setErrorMessage(message, button);
          return;
        }

        targets.forEach(function (target) {
          target.error.textContent = message;
          pcShow(target.error);
          pcHide(target.label);
        });

        clearTimeout(this.pcErrorTimer);
        this.pcErrorTimer = setTimeout(function () {
          targets.forEach(function (target) {
            pcHide(target.error);
            if (target.label) target.label.style.display = '';
          });
          self.pcErrorTimer = null;
        }, 2500);
      }

      /* ---------------------------------------------------------------
       * Loading state
       * ------------------------------------------------------------- */

      /**
       * assets/base.css:1
       *   .button.loading .loading-overlay__spinner { display: flex !important }
       *   .button--has-spinner:not(.loading) .loading-overlay__spinner { display: none }
       * The MAIN ATC spinner ships WITH .hidden (buy-buttons.liquid:150,
       * card-product.liquid:462, bundle-offer.liquid:682) so it must also be
       * unhidden; the secondary button (buy-buttons.liquid:176) and the sticky ATC
       * (main-product.liquid:668) rely on .button--has-spinner instead.
       */
      pcStartLoading(button) {
        if (!button) return null;
        var spinner = button.querySelector('.loading-overlay__spinner');
        var spinnerWasHidden = !!(spinner && spinner.classList.contains('hidden'));

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        if (spinner && spinnerWasHidden) spinner.classList.remove('hidden');

        return { button: button, spinner: spinner, spinnerWasHidden: spinnerWasHidden };
      }

      pcStopLoading(state) {
        if (!state || !state.button) return;
        state.button.classList.remove('loading');
        // Leave aria-disabled in place when the error path deliberately set it
        // (the sold-out branch below).
        if (state.button.dataset.pcSoldOut !== 'true') {
          state.button.removeAttribute('aria-disabled');
        }
        if (state.spinner && state.spinnerWasHidden) state.spinner.classList.add('hidden');
      }

      /* ---------------------------------------------------------------
       * Errors
       * ------------------------------------------------------------- */

      pcHandleCartError(parsedState, button) {
        var message = parsedState.description || pcCartError();

        // card-product.liquid:444 renders data-sold-out-message="true" with
        // span.sold-out-message.hidden at card-product.liquid:456-458.
        if (button && button.querySelector('.sold-out-message')) {
          button.setAttribute('aria-disabled', 'true');
          button.dataset.pcSoldOut = 'true';
          var label = button.querySelector('span');
          if (label) label.classList.add('hidden');
          var soldOut = button.querySelector('.sold-out-message');
          if (soldOut) soldOut.classList.remove('hidden');
          return;
        }

        this.setErrorMessage(message, button);
      }

      /**
       * Generic error surface. assets/base.css:1 keys off
       * `.product-form__error-message-wrapper:not([hidden])`, so we must remove
       * the ATTRIBUTE, not toggle a class. Present only at buy-buttons.liquid:65-78
       * and bundle-offer.liquid:641-654 — null-guarded everywhere else.
       */
      setErrorMessage(text, button) {
        if (this.errorWrapper && this.errorMessage) {
          this.errorMessage.textContent = text || '';
          this.errorWrapper.removeAttribute('hidden');
          return;
        }
        // No wrapper at this render site — fall back to the in-button span.
        if (this.querySelector('.main-atc__error')) {
          this.pcShowInButtonError(text, false, button);
        }
      }

      clearErrorMessage() {
        if (this.errorWrapper) this.errorWrapper.setAttribute('hidden', 'hidden');
        if (this.errorMessage) this.errorMessage.textContent = '';
        var mainError = this.querySelector('.main-atc__error');
        if (mainError) {
          pcHide(mainError);
          var mainLabel = this.querySelector('.main-atc__label');
          if (mainLabel) mainLabel.style.display = '';
        }
      }

      /* ---------------------------------------------------------------
       * Quantity rules ("N in cart")
       * ------------------------------------------------------------- */

      /**
       * sections/main-product.liquid:366-405 renders
       *   span.quantity__rules-cart.no-js-hidden[.hidden when cart_qty == 0]
       *     > span.loading-overlay.hidden
       *     > span > span.quantity-cart  (locale products.product.quantity.in_cart_html,
       *                                   locales/en.default.json:149)
       *   input#Quantity-<sectionId>[data-cart-quantity]
       * /cart/add.js returns the resulting line, so parsedState.quantity is the
       * new in-cart quantity for that variant.
       */
      pcSyncQuantityRules(lineItem) {
        if (!this.isMain || !lineItem || !this.sectionId) return;
        if (!this.variantIdInput) return;
        if (String(lineItem.id) !== String(this.variantIdInput.value)) return;

        var scope = this.pcScope;
        var quantityInput = scope.querySelector('#Quantity-' + CSS_ESCAPE(this.sectionId));
        if (quantityInput) quantityInput.dataset.cartQuantity = lineItem.quantity;

        var rules = scope.querySelector('.quantity__rules-cart');
        if (!rules) return;

        var counter = rules.querySelector('.quantity-cart');
        if (counter) counter.textContent = lineItem.quantity;

        if (lineItem.quantity > 0) rules.classList.remove('hidden');
        else rules.classList.add('hidden');
      }

      /* ---------------------------------------------------------------
       * In-button price mirroring
       * ------------------------------------------------------------- */

      /**
       * The in-button price is a the previous vendor addition with no Dawn equivalent:
       *   span#main-atc-price-<sectionId> > span.main-atc-price-span-<sectionId>
       *     buy-buttons.liquid:129-131 (left) and :145-147 (right)
       *   span#sticky-atc-price-<sectionId> > span.main-atc-price-span-<sectionId>
       *     sections/main-product.liquid:656-658
       * Both ship with `.hidden` pre-applied when the variant is unavailable.
       *
       * Rather than duplicating variant lookup + money formatting (this theme has
       * no global money_format), we mirror whatever the price block already shows.
       * #price-<sectionId> (main-product.liquid:171) is re-rendered by
       * <variant-selects>, so a MutationObserver on it keeps the button in sync no
       * matter which element performs the section render.
       */
      pcObservePriceBlock() {
        if (!this.isMain || !this.sectionId || typeof MutationObserver === 'undefined') return;

        var priceBlock = this.pcScope.querySelector('#price-' + CSS_ESCAPE(this.sectionId));
        if (!priceBlock) return;

        var self = this;
        this.pcPriceObserver = new MutationObserver(function () {
          pcSafe(function () {
            self.pcMirrorPrice(priceBlock);
          }, 'price mirror');
        });
        this.pcPriceObserver.observe(priceBlock, { childList: true, subtree: true });
      }

      pcMirrorPrice(priceBlock) {
        // snippets/price.liquid:57-66 renders div.price with the modifier classes.
        var priceRoot = priceBlock.querySelector('.price') || priceBlock;
        var onSale = priceRoot.classList.contains('price--on-sale');
        var soldOut = priceRoot.classList.contains('price--sold-out');

        var moneyEl = onSale
          ? priceRoot.querySelector('.price__sale .price-item--sale')
          : priceRoot.querySelector('.price__regular .price-item--regular');
        if (!moneyEl) return;

        var money = moneyEl.textContent.trim();
        if (money) this.updateButtonPrice(money);

        this.pcTogglePriceWrapper(soldOut);
      }

      pcTogglePriceWrapper(soldOut) {
        var ids = ['main-atc-price-' + this.sectionId, 'sticky-atc-price-' + this.sectionId];
        var scope = this.pcScope;
        ids.forEach(function (id) {
          var el = scope.querySelector('#' + CSS_ESCAPE(id));
          if (!el) return;
          if (soldOut) el.classList.add('hidden');
          else el.classList.remove('hidden');
        });
      }

      /** PUBLIC: write a money string into every in-button price span. */
      updateButtonPrice(text) {
        if (!this.sectionId) return;
        this.pcQueryScopeAll('.main-atc-price-span-' + CSS_ESCAPE(this.sectionId)).forEach(function (
          span
        ) {
          span.textContent = text;
        });
      }

      /* ---------------------------------------------------------------
       * Add-button state (public, Dawn-12 compatible signature)
       * ------------------------------------------------------------- */

      /**
       * Writes into span.main-atc__label__text (buy-buttons.liquid:133,
       * card-product.liquid:449) and span.sticky-atc__label__text
       * (main-product.liquid:660) — never into the whole button, which also holds
       * .main-atc__error, #main-atc-price-* and the spinner.
       *
       * Label when available: data-custom-label (buy-buttons.liquid:122-124) for
       * the main button, data-label (main-product.liquid:650) for the sticky one,
       * else window.variantStrings.addToCart (layout/theme.liquid:687).
       * Label when unavailable: window.variantStrings.soldOut (theme.liquid:688).
       */
      toggleAddButton(disable, text, modifyClass) {
        if (disable === undefined) disable = true;

        var strings = window.variantStrings || {};
        var buttons = [];

        var mainButton = this.querySelector('[type="submit"]');
        if (mainButton) {
          buttons.push({
            button: mainButton,
            label: mainButton.querySelector('.main-atc__label__text'),
            defaultLabel: mainButton.dataset.customLabel || strings.addToCart
          });
        }

        if (this.isMain && this.sectionId) {
          var sectionButton = this.pcQueryScope('#SectionAtcBtn-' + CSS_ESCAPE(this.sectionId));
          if (sectionButton && sectionButton !== mainButton) {
            buttons.push({
              button: sectionButton,
              label:
                sectionButton.querySelector('.sticky-atc__label__text') ||
                sectionButton.querySelector('.main-atc__label__text'),
              defaultLabel: sectionButton.dataset.label || strings.addToCart
            });
          }
        }

        buttons.forEach(function (entry) {
          if (disable) {
            entry.button.setAttribute('disabled', 'disabled');
            if (entry.label) entry.label.textContent = text || strings.soldOut || '';
          } else {
            entry.button.removeAttribute('disabled');
            if (entry.label) entry.label.textContent = text || entry.defaultLabel || '';
          }
        });

        if (modifyClass === false) return;
      }
    };
  };

  /* =======================================================================
   * <product-info>
   *
   * Deliberately thin. This theme is Dawn-12 shaped: <variant-selects>
   * (snippets/product-variant-picker.liquid:21-38) carries data-url,
   * data-section, data-update-url, data-skip-non-existent, data-skip-unavailable,
   * data-has-filtering and data-has-quantity-breaks-picker — the full set a
   * Dawn-12 VariantSelects.renderProductInfo() needs — while <product-info>
   * (sections/main-product.liquid:113-118) carries only id/data-section/data-url
   * plus its container class. So the fetch-and-swap lives in <variant-selects>;
   * this element is the DOM scope + a small public API.
   *
   * It MUST still be registered: assets/media-gallery.js:13 calls
   * this.productInfo.initShareLinks() inside <media-gallery>'s constructor, and
   * an unregistered tag is an HTMLUnknownElement with no methods — the call
   * would throw and abort the whole media gallery constructor.
   * ===================================================================== */

  var ProductInfoElement = function (BaseElement) {
    return class extends BaseElement {
      /** Quick-add safe. assets/quick-add.js:108-112 rewrites the section id to
       *  `quickadd-<sectionId>` in the cloned markup and stashes the true id in
       *  data-original-section on variant-selects, variant-radios and product-info. */
      get sectionId() {
        return this.dataset.originalSection || this.dataset.section || '';
      }

      /** Convenience for whichever component performs the variant section render. */
      sectionUrl(variantId) {
        var base = this.dataset.url || '';
        var params = [];
        if (variantId) params.push('variant=' + encodeURIComponent(variantId));
        if (this.sectionId) params.push('section_id=' + encodeURIComponent(this.sectionId));
        if (!params.length) return base;
        return base + (base.indexOf('?') === -1 ? '?' : '&') + params.join('&');
      }

      /** The variant currently staged in the buy form (buy-buttons.liquid:88-96). */
      get currentVariantId() {
        var input = this.querySelector('product-form input[name="id"]');
        return input ? input.value : '';
      }

      /**
       * REQUIRED PUBLIC NAME — assets/media-gallery.js:13:
       *   if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
       * Shopify.postLinksRetry appears nowhere else in the theme (it was a vendor
       * global), so this is effectively dead today — but it must exist and must
       * never throw, or a stray truthy flag would abort <media-gallery>.
       *
       * Best reconstruction from the surviving markup: re-point the share URL and
       * the "view details" link at the currently selected variant.
       */
      initShareLinks() {
        var self = this;
        pcSafe(function () {
          var variantId = self.currentVariantId;
          var base = self.dataset.url || window.location.pathname;
          var url =
            window.location.origin +
            base +
            (variantId ? '?variant=' + encodeURIComponent(variantId) : '');

          // snippets/share-button.liquid:15 + :28-36, assets/share.js:44-47.
          var shareButton = self.querySelector('share-button');
          if (shareButton && typeof shareButton.updateUrl === 'function') {
            shareButton.updateUrl(url);
          } else {
            var shareInput = self.querySelector('[id^="ShareUrl-"]');
            if (shareInput) shareInput.value = url;
          }

          // sections/main-product.liquid:1048-1051
          var viewDetails = self.querySelector('a.product__view-details');
          if (viewDetails) viewDetails.setAttribute('href', url);
        }, 'initShareLinks');
      }

      /**
       * assets/base.css:1 — `product-info .loading-overlay:not(.hidden) ~ *
       * { visibility: hidden }`. Its only matching markup is
       * span.loading-overlay.hidden inside .quantity__rules-cart
       * (sections/main-product.liquid:367).
       */
      toggleQuantityLoading(isLoading) {
        var overlay = this.querySelector('.quantity__rules-cart .loading-overlay');
        if (!overlay) return;
        if (isLoading) overlay.classList.remove('hidden');
        else overlay.classList.add('hidden');
      }
    };
  };

  /* =======================================================================
   * CSS.escape shim — ids here contain Shopify section ids such as
   * "template--28815977283716__main", which are valid in a selector, but the
   * quick-add clone prefixes them and block ids can contain characters that are
   * not. Cheap and dependency-free.
   * ===================================================================== */
  function CSS_ESCAPE(value) {
    var str = String(value);
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(str);
    return str.replace(/([^\w-])/g, '\\$1');
  }

  /* =======================================================================
   * Registration
   * ===================================================================== */

  if (!customElements.get('product-form')) {
    customElements.define('product-form', ProductFormElement(HTMLElement));
  }

  if (!customElements.get('product-info')) {
    customElements.define('product-info', ProductInfoElement(HTMLElement));
  }
})();
